import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import type { CriminalCoverNoteResponse } from '../../types/criminalApi';
import * as criminalService from '../../services/criminalCaseRequests';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { printCriminalCoverNotePdf } from '../../utils/criminalCoverNotePrint';
import { coverNoteQrImageSrc } from '../../utils/coverNoteQr';
import {
  criminalCoverNoteDivisionUnit,
  criminalCoverNoteSuspectsDisplay,
} from '../../utils/criminalCoverNoteDisplay';
import { setFlashToast } from '../../utils/flashToast';

type CriminalCoverNoteModalProps = {
  open: boolean;
  caseRequestId: string;
  fileRefFallback?: string;
  onClose: () => void;
};

function displayValue(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

function CoverNoteBody({ coverNote, qrSrc }: { coverNote: CriminalCoverNoteResponse; qrSrc: string }) {
  const { t } = useTranslation();

  return (
    <div className="cn-body">
      <div className="cn-meta cn-meta-qf">
        <div className="cn-qr-box">
          {qrSrc ? (
            <img src={qrSrc} alt={t('viewRequest.qrAlt')} width={68} height={68} style={{ display: 'block' }} />
          ) : (
            <div style={{ width: 68, height: 68, background: 'var(--bg)' }} aria-hidden />
          )}
          <div className="cn-qr-note">{t('caseRequests.qrNote')}</div>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.fileReferenceNumber')}</label>
          <p>{displayValue(coverNote.fileReferenceNumber)}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.requestSubmittedAt')}</label>
          <p>{formatDisplayDateTime(coverNote.requestSubmittedAt) || '—'}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.divisionUnit')}</label>
          <p>
            {displayValue(
              criminalCoverNoteDivisionUnit(coverNote.submittedDivisionName, coverNote.submittedAgency),
            )}
          </p>
        </div>
        <div className="cn-box">
          <label>{t('tasks.shared.fileCategory')}</label>
          <p className="cat-val">{displayValue(coverNote.fileCategory)}</p>
          {coverNote.subFileCategory?.trim() ? (
            <p className="sub">{t('caseRequests.subFileCategoryLabel', { value: coverNote.subFileCategory.trim() })}</p>
          ) : null}
        </div>
      </div>

      <div className="cn-sum-title">{t('caseRequests.summaryTitle')}</div>
      <div className="cn-sum-grid">
        <div className="cn-col">
          <div className="csf">
            <label>{t('caseRequests.caseSource')}</label>
            <p>{displayValue(coverNote.caseSource)}</p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.connectedRef')}</label>
            <p>{displayValue(coverNote.connectedAgdFileReferenceNumber)}</p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.accusedName')}</label>
            <p>
              {displayValue(
                criminalCoverNoteSuspectsDisplay(
                  coverNote.suspects,
                  coverNote.accusedOrSuspectName,
                  coverNote.accusedOrSuspectStatus,
                ),
              )}
            </p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.applicableAreas')}</label>
            <p>{displayValue(coverNote.applicableAreas)}</p>
          </div>
          <div className="csf">
            <label>{t('wizard.fields.description')}</label>
            <p>{displayValue(coverNote.description)}</p>
          </div>
        </div>
        <div className="cn-col">
          <div className="csf">
            <label>{t('caseRequests.urgencyLevel')}</label>
            <p>{displayValue(coverNote.urgencyLevel)}</p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.deadline')}</label>
            <p>{formatDisplayDate(coverNote.deadline) || '—'}</p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.urgencyJustification')}</label>
            <p>{displayValue(coverNote.urgencyJustification)}</p>
          </div>
          <div className="csf">
            <label>{t('caseRequests.documentsDeclared')}</label>
            <div className="dtags">
              {(coverNote.documents?.length ? coverNote.documents : ['None']).map((doc, index) => (
                <div key={`${doc}-${index}`} className="dtag">
                  {doc !== 'None' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                  {doc}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type CriminalCoverNoteModalContentProps = {
  caseRequestId: string;
  fileRefFallback?: string;
  onClose: () => void;
};

function CriminalCoverNoteModalContent({
  caseRequestId,
  fileRefFallback,
  onClose,
}: CriminalCoverNoteModalContentProps) {
  const { t } = useTranslation();
  const id = caseRequestId.trim();
  const [coverNote, setCoverNote] = useState<CriminalCoverNoteResponse | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!id) return;

    let mounted = true;
    void criminalService.getCriminalCoverNote(id).then((r) => {
      if (!mounted) return;
      setLoadedKey(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('caseRequests.coverNoteLoadFailed'), variant: 'error' });
        return;
      }
      setCoverNote(r.data);
    });

    return () => {
      mounted = false;
    };
  }, [id, t]);

  const loading = Boolean(id) && loadedKey !== id;
  const fileRef = coverNote?.fileReferenceNumber?.trim() || fileRefFallback?.trim() || '—';

  const downloadPdf = async () => {
    if (downloading || !coverNote || !id) return;

    setDownloading(true);
    try {
      const r = await criminalService.downloadCriminalCoverNotePdf(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('viewRequest.coverNotePdfFailed'), variant: 'error' });
        return;
      }

      const pdfBlob =
        r.data.type === 'application/pdf' ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const safeRef = fileRef.replace(/[^\w.-]+/g, '_');
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cover-Note-${safeRef}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const printCoverNote = async () => {
    if (!coverNote || printing || !id) return;
    setPrinting(true);
    try {
      const r = await printCriminalCoverNotePdf(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? t('viewRequest.coverNotePdfFailed'), variant: 'error' });
      }
    } finally {
      setPrinting(false);
    }
  };

  if (!id) {
    return (
      <>
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </>
    );
  }

  const qrSrc = coverNote ? coverNoteQrImageSrc(coverNote.qr) : '';

  if (loading) {
    return (
      <>
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.loadingCoverNote')}
        </div>
      </>
    );
  }

  if (!coverNote) {
    return (
      <>
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="cn-printable">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <CoverNoteBody coverNote={coverNote} qrSrc={qrSrc} />
      </div>
      <div className="cn-footer cn-footer--plain">
        <button className="mfb" type="button" disabled={downloading} onClick={() => void downloadPdf()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {downloading ? t('caseRequests.downloading') : t('caseRequests.downloadPdf')}
        </button>
        <button className="mfb" type="button" disabled={printing} onClick={() => void printCoverNote()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {printing ? t('tasks.details.preparing') : t('caseRequests.print')}
        </button>
        <button className="mfb mfb-dk" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </>
  );
}

export function CriminalCoverNoteModal({
  open,
  caseRequestId,
  fileRefFallback,
  onClose,
}: CriminalCoverNoteModalProps) {
  return (
    <Modal open={open} onClose={onClose} boxClassName="cn-mbox-cover-note">
      {open ? (
        <CriminalCoverNoteModalContent
          key={caseRequestId}
          caseRequestId={caseRequestId}
          fileRefFallback={fileRefFallback}
          onClose={onClose}
        />
      ) : null}
    </Modal>
  );
}
